import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import nafHubLogoMobile from "assets/images/v2/naf-hub-logo-mobile.svg";
import nafHubLogoDesktop from "assets/images/v2/naf-hub-logo.svg";
import ProfileMenu from "../profile-menu/ProfileMenu";
import "./GlobalHeader.scss";
import ProfileMenuDropdown from "../profile-menu-dropdown/ProfileMenuDropdown";
import useDesktopChecker from "hooks/useDesktopChecker";
import { ReactComponent as NotificationsIcon } from "assets/images/v2/notification-bell-icon.svg";
import { PalNotificationHandler } from "../notifications";
import { useNotificationContext } from "contexts/NotificationContext";
import useTabletChecker from "hooks/useTabletChecker";
import { useSelector } from "react-redux";
import {
  getCurrentAccessToken,
  getCurrentUserName,
  getDealId,
  getEncompassLoanId,
  getIsImpersonating,
  getLoanId,
  getLoanNumber,
  getProspectId,
} from "features/authSlice";
import NativeAppBannerCard from "../native-app-banner-card/NativeAppBannerCard";
import { useNativeAppPromoVisibility } from "hooks/useNativeAppPromoVisibility";
import PreferredLanguage from "components/v2/preferred-language";
import UqualAdBanner from "../uqual-ad-banner/UqualAdBanner";
import { useUqualAdBannerVisibility } from "hooks/useUqualAdBannerVisibility";
import UqualCard from "pages/dashboard/v2/uqual-card/UqualCard";
import useLoginPreferredLanguageSync from "components/auth-routing/useLoginPreferredLanguageSync";
import { useUqualFullStoryTrackingProps } from "hooks/useUqualFullStoryTrackingProps";

interface GlobalHeaderProps {
  onNotificationsClick?: () => void;
  onDocumentNotificationClick?: (documentId: string) => void;
  isNavOpen?: boolean;
  setIsNavOpen?: (open: boolean) => void;
  showLogoOnDesktop?: boolean;
  hideNotifications?: boolean;
  uqualPanelOpenerRef?: React.MutableRefObject<(() => void) | undefined>;
}

const GlobalHeader: React.FC<GlobalHeaderProps> = ({ onNotificationsClick, onDocumentNotificationClick, isNavOpen, setIsNavOpen, showLogoOnDesktop = false, hideNotifications = false, uqualPanelOpenerRef, }: GlobalHeaderProps) => {
  useLoginPreferredLanguageSync();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isUserAuthenticated = useSelector(getCurrentAccessToken);
  const { isBannerVisible, dismissBanner } = useNativeAppPromoVisibility();
  const { isBannerVisible: isUqualBannerVisible, dismissBanner: dismissUqualBanner } = useUqualAdBannerVisibility();
  const bannerVariant: "desktop" | "mobile" = isDesktop || isTablet ? "desktop" : "mobile";
  const isImpersonating = useSelector(getIsImpersonating);
  const currentUserName = useSelector(getCurrentUserName);
  const prospectId = useSelector(getProspectId);
  const dealId = useSelector(getDealId);
  const loanNumber = useSelector(getLoanNumber);
  const encompassLoanId = useSelector(getEncompassLoanId);
  const loanId = useSelector(getLoanId);

  const nativeAppTrackingProps = useMemo(
    () => ({
      username: currentUserName,
      prospectId,
      dealId,
      losLoanNumber: loanNumber,
      losGuid: encompassLoanId || loanId,
    }),
    [currentUserName, prospectId, dealId, loanNumber, encompassLoanId, loanId]
  );

  const uqualTrackingProps = useUqualFullStoryTrackingProps();

  const [isUqualPanelOpen, setIsUqualPanelOpen] = useState(false);

  const handleUqualCtaClick = useCallback(() => {
    setIsUqualPanelOpen(true);
  }, []);

  useEffect(() => {
    if (!uqualPanelOpenerRef) return;
    uqualPanelOpenerRef.current = handleUqualCtaClick;
    return () => {
      uqualPanelOpenerRef.current = undefined;
    };
  }, [handleUqualCtaClick, uqualPanelOpenerRef]);

  const { notifications, removeNotification, hasNotifications } = useNotificationContext();

  const toggleDropdown = () => {
    setIsProfileMenuOpen((prev) => !prev);
  };

  const handleNotificationsClick = () => {
    if (onNotificationsClick) {
      onNotificationsClick();
      return;
    }

    setIsNotificationsOpen(!isNotificationsOpen);
  };

  const handleNotificationItemClick = async (notificationId: string) => {
    await removeNotification(notificationId);
    setIsNotificationsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  const nafHubLogoSrc = showLogoOnDesktop && isDesktop ? nafHubLogoDesktop : nafHubLogoMobile;

  const desktopNativeBanner = useMemo(
    () =>
      isDesktop && isBannerVisible && !isUqualBannerVisible ? (
        <NativeAppBannerCard
          className="global-header__app-banner global-header__app-banner--in-header"
          onDismiss={dismissBanner}
          variant="desktop"
          trackingProps={nativeAppTrackingProps}
        />
      ) : null,
    [isDesktop, isBannerVisible, dismissBanner, isUqualBannerVisible, nativeAppTrackingProps]
  );

  const mobileNativeBanner = useMemo(
    () =>
      !isDesktop && isBannerVisible && !isUqualBannerVisible ? (
        <div className="global-header__app-banner--wrapper">
          <NativeAppBannerCard
            className="global-header__app-banner global-header__app-banner--below-header"
            onDismiss={dismissBanner}
            variant={bannerVariant}
            trackingProps={nativeAppTrackingProps}
          />
        </div>
      ) : null,
    [isDesktop, isBannerVisible, dismissBanner, bannerVariant, isUqualBannerVisible, nativeAppTrackingProps]
  );

  const desktopUqualBanner = useMemo(
    () =>
      isDesktop && isUqualBannerVisible ? (
        <UqualAdBanner
          className="global-header__app-banner global-header__app-banner--in-header"
          onCTAClick={handleUqualCtaClick}
          onDismiss={dismissUqualBanner}
          trackingProps={uqualTrackingProps}
        />
      ) : null,
    [isDesktop, isUqualBannerVisible, dismissUqualBanner, handleUqualCtaClick, uqualTrackingProps]
  );

  const mobileUqualBanner = useMemo(
    () =>
      !isDesktop && isUqualBannerVisible ? (
        <div className="global-header__app-banner--wrapper">
          <UqualAdBanner
            className="global-header__app-banner global-header__app-banner--below-header"
            onCTAClick={handleUqualCtaClick}
            onDismiss={dismissUqualBanner}
            trackingProps={uqualTrackingProps}
          />
        </div>
      ) : null,
    [isDesktop, isUqualBannerVisible, dismissUqualBanner, handleUqualCtaClick, uqualTrackingProps]
  );

  return (
    <>
      <div className={`global-header${isImpersonating ? " global-header--impersonating" : ""}`}>
        {!isDesktop && isTablet && setIsNavOpen && (
          <>
            {!isNavOpen && (
              <button
                type="button"
                className="mobile-nav__hamburger navExpandButton"
                aria-label="Open navigation"
                aria-expanded={false}
                aria-controls="tablet-nav-overlay-panel"
                onClick={() => setIsNavOpen(true)}
              >
                <span>&#9776;</span>
              </button>
            )}
            {isNavOpen && (
              <button
                type="button"
                className="mobile-nav__close navExpandButton"
                aria-label="Close navigation"
                aria-expanded
                aria-controls="tablet-nav-overlay-panel"
                onClick={() => setIsNavOpen(false)}
              >
                <span>&#10005;</span>
              </button>
            )}
          </>
        )}

        {(showLogoOnDesktop || !isDesktop) && (
          <img
            className={`global-header__logo${showLogoOnDesktop && isDesktop ? "__desktop" : ""}${isUserAuthenticated ? "" : "__un-authorized"}`}
            src={nafHubLogoSrc}
            alt="NAF Hub"
          />
        )}
        {desktopNativeBanner}
        {desktopUqualBanner}
        <div className="global-header__controls">
           <div className="global-header__controls__preferred-language" ><PreferredLanguage isMobile={!isDesktop} isAuthenticated={Boolean(isUserAuthenticated)} /></div>
          {!hideNotifications && (
            <button className="global-header__notification-button" onClick={handleNotificationsClick}>
              <NotificationsIcon />
              {hasNotifications && <span className="new" />}
            </button>
          )}
          {isUserAuthenticated && <ProfileMenu onClick={toggleDropdown} />}
          {(isProfileMenuOpen || isNotificationsOpen) && (
            <div className={`global-header__content ${isNotificationsOpen ? 'notifications' : 'profile'}`} ref={dropdownRef}>
              {isProfileMenuOpen && <ProfileMenuDropdown />}
              {isNotificationsOpen && (
                <div className="global-header__notifications-content">
                  <PalNotificationHandler
                    notifications={notifications}
                    onNotificationClick={handleNotificationItemClick}
                    onDocumentNotificationClick={onDocumentNotificationClick}
                    onUqualNotificationClick={handleUqualCtaClick}
                    isOpen={isNotificationsOpen}
                    onClose={() => setIsNotificationsOpen(false)}
                    triggerElementSelector=".global-header__notification-button"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {mobileNativeBanner}
      {mobileUqualBanner}
      {isUqualPanelOpen && (
        <UqualCard
          open={isUqualPanelOpen}
          onClose={() => setIsUqualPanelOpen(false)}
          hideCard
          trackingProps={uqualTrackingProps}
        />
      )}
    </>
  );
};

export default GlobalHeader;
